import {useMemo, useEffect} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';
import {
  LiveAnswersClient,
  LiveAnswersMessage,
} from '@stripe-internal/conversations-client';
import {LiveAnswersPoller} from '../lib/LiveAnswersPoller';

/**
 * React hook for LiveAnswersPoller with automatic cleanup.
 * Returns poller controls and subscribes to state updates.
 */
export function useLiveAnswersPoller(
  liveAnswersClient: LiveAnswersClient,
  liveAnswersMessage: LiveAnswersMessage,
) {
  const pollerInstance = useMemo(() => {
    return new LiveAnswersPoller(liveAnswersClient, liveAnswersMessage);
  }, [liveAnswersClient, liveAnswersMessage]);

  // expose just the interface, so hook users don't interact with the class directly
  const poller = useMemo(
    () => ({
      startPolling: () => pollerInstance.startPolling(),
      stopPolling: () => pollerInstance.stopPolling(),
    }),
    [pollerInstance],
  );

  const state = useSyncExternalStore(
    (callback) => pollerInstance.subscribe(callback),
    () => pollerInstance.getState(),
  );

  useEffect(() => {
    return () => {
      pollerInstance.destroy();
    };
  }, [pollerInstance]);

  return {
    ...state,
    poller,
  };
}
